import {
  BlueprintStoreyAssignment,
  BlueprintUnderlay,
  PersistedBlueprintStoreyAssignment,
} from '@shared/models/design/blueprint-underlay.model';
import type { BuildingDesign } from '@shared/models/design/building-design.model';
import type { Storey } from '@shared/models/design/storey.model';
import { Point2D } from '@shared/models/geometry.model';
import { v4 } from 'uuid';
import { getStoreyElevation } from './storey.helpers';
import {
  areBlueprintStoreyAssignmentsEqual,
  blueprintStoreyAssignmentFromStartLevel,
  getBlueprintStoreyAssignmentSpecificity,
  getBlueprintStoreyAssignmentStartLevel,
  getBlueprintStoreyStackSize,
  isLegacyStoreyAssignment,
  normalizeBlueprintStoreyAssignment,
  resolveBlueprintUnderlayStoreySpans,
  type StoreyForAssignment,
} from './blueprint-underlay-storey.helpers';
import {
  DEFAULT_BLUEPRINT_OPACITY,
  DEFAULT_BLUEPRINT_WIDTH_METERS,
  inferBlueprintPaperSizeMm,
} from './blueprint-underlay-geometry.helpers';

/** Legacy singular underlay shape before multi-page migration. */
type LegacyBlueprintUnderlay = Omit<
  BlueprintUnderlay,
  'id' | 'storeyAssignment'
> & {
  id?: string;
  storeyAssignment?: PersistedBlueprintStoreyAssignment;
};

/** Underlay row that may still carry a pre-kind storey assignment. */
type PersistedBlueprintUnderlay = Omit<
  BlueprintUnderlay,
  'storeyAssignment'
> & {
  id?: string;
  storeyAssignment?: PersistedBlueprintStoreyAssignment;
};

/** Design that may still carry singular underlay / legacy assignment shapes. */
export type DesignWithLegacyBlueprintUnderlay = Omit<
  BuildingDesign,
  'blueprintUnderlays'
> & {
  blueprintUnderlay?: LegacyBlueprintUnderlay;
  blueprintUnderlays?: PersistedBlueprintUnderlay[];
};

/**
 * Display name for a blueprint underlay: optional sheet label, else file name
 * (extension stripped), else a page label.
 */
export const getBlueprintDisplayName = (
  underlay: Pick<BlueprintUnderlay, 'fileName' | 'pageIndex' | 'label'>,
): string => {
  const label = underlay.label?.trim();
  if (label) return label;
  const raw = underlay.fileName?.trim();
  if (!raw) return `Page ${underlay.pageIndex + 1}`;
  const base = raw.replace(/\.[^.]+$/u, '').trim();
  return base || raw;
};

/**
 * Ensure a underlay has a stable id and normalized storey assignment.
 * Returns the same reference when identity fields are already valid.
 * @param underlay Legacy or current underlay shape.
 * @param fallbackLevel 1-based level used when assignment is missing.
 */
export const ensureBlueprintUnderlayIdentity = (
  underlay: LegacyBlueprintUnderlay,
  fallbackLevel = 1,
): BlueprintUnderlay => {
  const storeyAssignment = normalizeBlueprintStoreyAssignment(
    underlay.storeyAssignment,
    fallbackLevel,
  );
  // Keep reference identity so selectors/effects do not thrash on every read.
  if (
    underlay.id &&
    underlay.storeyAssignment &&
    !isLegacyStoreyAssignment(underlay.storeyAssignment) &&
    areBlueprintStoreyAssignmentsEqual(
      underlay.storeyAssignment,
      storeyAssignment,
    )
  ) {
    return underlay as BlueprintUnderlay;
  }
  return {
    ...underlay,
    id: underlay.id ?? v4(),
    storeyAssignment,
  };
};

/**
 * Normalize persisted underlays, including legacy singular `blueprintUnderlay`.
 * Returns the same array reference when every entry is already normalized.
 */
export const getNormalizedBlueprintUnderlays = (
  design: DesignWithLegacyBlueprintUnderlay,
): BlueprintUnderlay[] => {
  if (design.blueprintUnderlays?.length) {
    const underlays = design.blueprintUnderlays;
    let changed = false;
    const next = underlays.map((underlay, index) => {
      const normalized = ensureBlueprintUnderlayIdentity(underlay, index + 1);
      if (normalized !== underlay) changed = true;
      return normalized;
    });
    return changed ? next : (underlays as BlueprintUnderlay[]);
  }
  if (design.blueprintUnderlay) {
    return [ensureBlueprintUnderlayIdentity(design.blueprintUnderlay, 1)];
  }
  return [];
};

/**
 * Find a normalized underlay by id, including legacy singular `blueprintUnderlay`.
 */
export const findBlueprintUnderlayById = (
  design: DesignWithLegacyBlueprintUnderlay,
  underlayId: string,
): BlueprintUnderlay | undefined =>
  getNormalizedBlueprintUnderlays(design).find(
    (underlay) => underlay.id === underlayId,
  );

/**
 * Next unused 1-based storey level in the building stack (UI numbering).
 * When {@link storeys} is empty, falls back to one past the highest used level.
 */
export const getNextFreeBlueprintStoreyLevel = (
  underlays: readonly BlueprintUnderlay[],
  storeys: readonly StoreyForAssignment[] = [],
): number => {
  // Only start levels count as “used” — coverage until the next page is implicit.
  const used = new Set<number>();
  for (const underlay of underlays) {
    used.add(
      getBlueprintStoreyAssignmentStartLevel(
        underlay.storeyAssignment,
        storeys,
      ),
    );
  }
  const maxLevel =
    storeys.length > 0
      ? getBlueprintStoreyStackSize(storeys)
      : Math.max(1, ...used, 0) + 1;
  for (let level = 1; level <= maxLevel; level++) {
    if (!used.has(level)) return level;
  }
  return maxLevel;
};

/**
 * Default single-level assignment for a newly placed page.
 * Prefers the selected storey’s level, else the next free level.
 */
export const getDefaultBlueprintStoreyAssignment = (options: {
  underlays: readonly BlueprintUnderlay[];
  selectedStoreyLevel?: number;
  storeys?: readonly StoreyForAssignment[];
}): BlueprintStoreyAssignment => {
  const storeys = options.storeys ?? [];
  const maxLevel =
    storeys.length > 0 ? getBlueprintStoreyStackSize(storeys) : undefined;
  const rawLevel =
    options.selectedStoreyLevel !== undefined &&
    options.selectedStoreyLevel >= 1
      ? Math.floor(options.selectedStoreyLevel)
      : getNextFreeBlueprintStoreyLevel(options.underlays, storeys);
  const level =
    maxLevel !== undefined
      ? Math.min(maxLevel, Math.max(1, rawLevel))
      : Math.max(1, rawLevel);
  return { kind: 'otherStoreys', levels: [level] };
};

/** Multi-page import defaults by ordinal among selected pages (page order). */
const MULTI_PAGE_IMPORT_ASSIGNMENTS = [
  { kind: 'groundFloor' },
  { kind: 'typicalStoreys' },
  { kind: 'topFloor' },
] as const satisfies readonly BlueprintStoreyAssignment[];

/**
 * Default storey role for the Nth selected page in a multi-page PDF import
 * (0 = ground floor, 1 = typical, 2 = top; further pages → otherStoreys).
 */
export const getMultiPageImportStoreyAssignment = (
  ordinalAmongSelected: number,
): BlueprintStoreyAssignment => {
  const preset = MULTI_PAGE_IMPORT_ASSIGNMENTS[ordinalAmongSelected];
  if (preset) return { kind: preset.kind };
  return { kind: 'otherStoreys', levels: [ordinalAmongSelected + 1] };
};

/**
 * 1-based storey level for a storey id (`storeys[0]` → 1).
 */
export const getStoreyLevel = (
  storeys: readonly { id: string }[],
  storeyId: string | undefined,
): number | undefined => {
  if (!storeyId) return undefined;
  const index = storeys.findIndex((storey) => storey.id === storeyId);
  if (index < 0) return undefined;
  return index + 1;
};

/**
 * Underlay covering {@link level}, preferring higher assignment specificity.
 * Equal specificity → later list entry wins.
 */
export const findUnderlayForStoreyLevel = (
  underlays: readonly BlueprintUnderlay[],
  level: number,
  storeys: readonly StoreyForAssignment[] = [],
): BlueprintUnderlay | undefined => {
  const spans = resolveBlueprintUnderlayStoreySpans(underlays, storeys);
  let best: BlueprintUnderlay | undefined;
  let bestScore = -1;
  for (const underlay of underlays) {
    const span = spans.get(underlay.id);
    if (!span || level < span.fromLevel || level > span.toLevel) continue;
    const score = getBlueprintStoreyAssignmentSpecificity(
      underlay.storeyAssignment,
    );
    if (score < bestScore) continue;
    // Later entries win ties so a more recently placed page can override.
    best = underlay;
    bestScore = score;
  }
  return best;
};

/**
 * Closest page whose start storey is strictly below {@link level}.
 * Highest start wins; same start → specificity, then later list entry.
 */
export const findNearestUnderlayBelowStoreyLevel = (
  underlays: readonly BlueprintUnderlay[],
  level: number,
  storeys: readonly StoreyForAssignment[] = [],
): BlueprintUnderlay | undefined => {
  let best: BlueprintUnderlay | undefined;
  let bestStart = -1;
  let bestScore = -1;
  for (const underlay of underlays) {
    const start = getBlueprintStoreyAssignmentStartLevel(
      underlay.storeyAssignment,
      storeys,
    );
    if (start >= level) continue;
    const score = getBlueprintStoreyAssignmentSpecificity(
      underlay.storeyAssignment,
    );
    if (start < bestStart) continue;
    if (start === bestStart && score < bestScore) continue;
    best = underlay;
    bestStart = start;
    bestScore = score;
  }
  return best;
};

/**
 * Resolve which underlay the map should show: manual override, else the page
 * assigned to the selected storey, else the nearest page assigned below it.
 */
export const resolveActiveBlueprintUnderlay = (input: {
  underlays: readonly BlueprintUnderlay[];
  selectedStoreyId?: string;
  storeys: readonly StoreyForAssignment[];
  manualUnderlayId?: string;
}): BlueprintUnderlay | undefined => {
  const { underlays, selectedStoreyId, storeys, manualUnderlayId } = input;
  if (!underlays.length) return undefined;

  if (manualUnderlayId) {
    const manual = underlays.find(
      (underlay) => underlay.id === manualUnderlayId,
    );
    if (manual) return manual;
  }

  const level = getStoreyLevel(storeys, selectedStoreyId);
  if (level !== undefined) {
    return (
      findUnderlayForStoreyLevel(underlays, level, storeys) ??
      findNearestUnderlayBelowStoreyLevel(underlays, level, storeys)
    );
  }

  return underlays[0];
};

/**
 * Duplicate a page onto another start storey. The copy keeps the source
 * `imageRef`; persist then copies the blob to the new id's canonical key.
 */
export const copyBlueprintUnderlayToStoreyLevel = (
  underlay: BlueprintUnderlay,
  startLevel: number,
): BlueprintUnderlay => ({
  ...underlay,
  id: v4(),
  storeyAssignment: blueprintStoreyAssignmentFromStartLevel(startLevel),
});

/**
 * 1-based storey level used for the underlay’s elevation on the map.
 * Uses the page’s start storey from the Storey dropdown.
 */
export const getBlueprintUnderlayDisplayStoreyLevel = (
  assignment: BlueprintStoreyAssignment,
  storeys: readonly StoreyForAssignment[],
): number => getBlueprintStoreyAssignmentStartLevel(assignment, storeys);

/**
 * Map elevation (meters) for an underlay plane from its assigned start storey.
 * The plane stays on that elevation even when a higher storey is viewing it.
 */
export const getBlueprintUnderlayDisplayElevationMeters = (
  underlay: Pick<BlueprintUnderlay, 'storeyAssignment'>,
  storeys: readonly Storey[],
): number => {
  const startLevel = getBlueprintUnderlayDisplayStoreyLevel(
    underlay.storeyAssignment,
    storeys,
  );
  const storey = storeys[startLevel - 1];
  if (!storey) return 0;
  // getStoreyElevation takes a mutable Storey[] / BuildingDesign.
  return getStoreyElevation([...storeys], storey);
};

/**
 * Replace or append an underlay by id.
 */
export const upsertBlueprintUnderlay = (
  underlays: readonly BlueprintUnderlay[],
  next: BlueprintUnderlay,
): BlueprintUnderlay[] => {
  const index = underlays.findIndex((underlay) => underlay.id === next.id);
  if (index < 0) return [...underlays, next];
  return underlays.map((underlay, i) => (i === index ? next : underlay));
};

/**
 * Replace an existing underlay by id without recreating a deleted page.
 */
export const updateBlueprintUnderlayById = (
  underlays: readonly BlueprintUnderlay[],
  next: BlueprintUnderlay,
): BlueprintUnderlay[] =>
  underlays.map((underlay) => (underlay.id === next.id ? next : underlay));

/**
 * Remove one underlay by id.
 */
export const removeBlueprintUnderlayById = (
  underlays: readonly BlueprintUnderlay[],
  id: string,
): BlueprintUnderlay[] => underlays.filter((underlay) => underlay.id !== id);

/**
 * Write `blueprintUnderlays` onto a design and drop any legacy singular field.
 */
export const withBlueprintUnderlays = (
  design: DesignWithLegacyBlueprintUnderlay,
  underlays: readonly BlueprintUnderlay[],
): BuildingDesign => {
  const { blueprintUnderlay: _legacy, ...rest } = design;
  if (!underlays.length) {
    const { blueprintUnderlays: _removed, ...without } = rest;
    return without;
  }
  return { ...rest, blueprintUnderlays: [...underlays] };
};

/**
 * True when a persisted row still needs normalizing: missing identity, or a
 * pre-kind `{ fromLevel, toLevel }` assignment that read paths translate on
 * every access.
 */
const needsUnderlayIdentityRepair = (
  underlay: PersistedBlueprintUnderlay,
): boolean => {
  if (!underlay.id) return true;
  if (!underlay.storeyAssignment) return true;
  return isLegacyStoreyAssignment(underlay.storeyAssignment);
};

/**
 * Migrate singular `blueprintUnderlay` → `blueprintUnderlays[]` on a design.
 * @returns Same reference when unchanged.
 */
export const migrateBlueprintUnderlaysInDesign = <
  T extends DesignWithLegacyBlueprintUnderlay,
>(
  design: T,
): T => {
  const hasLegacy = design.blueprintUnderlay !== undefined;
  const existing = design.blueprintUnderlays;
  const needsIdentity = existing?.some(needsUnderlayIdentityRepair) ?? false;

  if (!hasLegacy && !needsIdentity) {
    if (existing && existing.length === 0) {
      const { blueprintUnderlays: _empty, ...rest } = design;
      return rest as T;
    }
    return design;
  }

  const underlays = getNormalizedBlueprintUnderlays(design);
  return withBlueprintUnderlays(design, underlays) as T;
};

/**
 * Build a new underlay from a rasterized page, centered at the origin with default scale.
 */
export const createBlueprintUnderlay = (input: {
  imageDataUrl: string;
  pageIndex: number;
  fileName?: string;
  /** Pixel width / height of the raster (used for aspect ratio). */
  pixelWidth: number;
  pixelHeight: number;
  paperWidthMm?: number;
  paperHeightMm?: number;
  widthMeters?: number;
  /**
   * When set with a known/inferred paper width, sizes the sheet to this `1:N`
   * drawing scale (e.g. inherit calibrated scale from an earlier underlay).
   */
  drawingScaleRatio?: number;
  /** Optional map center in local meters (defaults to origin). */
  center?: Point2D;
  /** Optional clockwise rotation in degrees (defaults to 0). */
  rotationDeg?: number;
  opacity?: number;
  label?: string;
  storeyAssignment?: BlueprintStoreyAssignment;
}): BlueprintUnderlay => {
  const aspect =
    input.pixelHeight > 0 && input.pixelWidth > 0
      ? input.pixelHeight / input.pixelWidth
      : 1;

  const inferredPaper = inferBlueprintPaperSizeMm(
    input.pixelWidth || 1,
    input.pixelHeight || 1,
  );
  const paperWidthMm =
    input.paperWidthMm && input.paperWidthMm > 0
      ? input.paperWidthMm
      : inferredPaper.paperWidthMm;
  const paperHeightMm =
    input.paperHeightMm && input.paperHeightMm > 0
      ? input.paperHeightMm
      : inferredPaper.paperHeightMm;

  let widthMeters = input.widthMeters ?? DEFAULT_BLUEPRINT_WIDTH_METERS;
  if (
    input.drawingScaleRatio !== undefined &&
    input.drawingScaleRatio > 0 &&
    paperWidthMm > 0
  ) {
    widthMeters = (input.drawingScaleRatio * paperWidthMm) / 1000;
  }

  return {
    id: v4(),
    imageDataUrl: input.imageDataUrl,
    pageIndex: input.pageIndex,
    fileName: input.fileName,
    label: input.label,
    storeyAssignment: normalizeBlueprintStoreyAssignment(
      input.storeyAssignment,
      1,
    ),
    pixelWidth: input.pixelWidth,
    pixelHeight: input.pixelHeight,
    paperWidthMm,
    paperHeightMm,
    center: input.center ? [input.center[0], input.center[1]] : [0, 0],
    widthMeters,
    heightMeters: widthMeters * aspect,
    rotationDeg: input.rotationDeg ?? 0,
    opacity: input.opacity ?? DEFAULT_BLUEPRINT_OPACITY,
    visible: true,
  };
};
